import React, { useState, useId } from "react";
import "./MoneyClarity.css";
import SimpleHeader from "../NewApp/SimpleHeader";
import Footer from "../Footer/Footer";
import mixpanel from "../../mixpanel";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

// ─── Types ───────────────────────────────────────────────────────────────────

type PriceCurrency = "AMD" | "USD";

interface Preset {
  id: string;
  name: string;
  emoji: string;
  price: number;
  currency: PriceCurrency;
  category: string;
  note?: string;
}

interface CustomExpense {
  uid: string;
  name: string;
  amount: string; // raw input
  period: "daily" | "monthly";
}

// ─── Subscription presets (Armenia pricing, approximate 2025) ─────────────────

const AMD_PER_USD = 390;

const PRESETS: Preset[] = [
  // Music
  { id: "spotify",        name: "Spotify",         emoji: "🎵", price: 2.99,  currency: "USD", category: "Երաժշտություն" },
  { id: "apple_music",    name: "Apple Music",      emoji: "🎶", price: 2.99,  currency: "USD", category: "Երաժշտություն" },
  { id: "yandex_plus",    name: "Yandex Plus",      emoji: "🎧", price: 990,   currency: "AMD", category: "Երաժշտություն" },
  // Video
  { id: "youtube_premium",name: "YouTube Premium",  emoji: "▶️", price: 2.99,  currency: "USD", category: "Վիդեո" },
  { id: "netflix",        name: "Netflix",           emoji: "🎬", price: 6.99,  currency: "USD", category: "Վիդեո", note: "Standard" },
  // Cloud
  { id: "icloud_50",      name: "iCloud 50 GB",     emoji: "☁️", price: 490,   currency: "AMD", category: "Cloud" },
  { id: "icloud_200",     name: "iCloud 200 GB",    emoji: "☁️", price: 990,   currency: "AMD", category: "Cloud" },
  { id: "icloud_2tb",     name: "iCloud 2 TB",      emoji: "☁️", price: 3990,  currency: "AMD", category: "Cloud" },
  // AI / Tools
  { id: "chatgpt",        name: "ChatGPT Plus",     emoji: "🤖", price: 20,    currency: "USD", category: "AI / Գործիքներ" },
  { id: "canva",          name: "Canva Pro",         emoji: "🎨", price: 15,    currency: "USD", category: "AI / Գործիքներ" },
  // Education
  { id: "duolingo",       name: "Duolingo Super",   emoji: "🦉", price: 6.99,  currency: "USD", category: "Կրթություն" },
];

const CATEGORIES = Array.from(new Set(PRESETS.map((p) => p.category)));

// ─── Finance helpers ───────────────────────────────────────────────────────────

const SP500_ANNUAL_RETURN = 0.10; // 10 % historical average

function sp500FV(monthlyAMD: number, years: number): number {
  if (monthlyAMD <= 0) return 0;
  const r = SP500_ANNUAL_RETURN / 12;
  const n = years * 12;
  return monthlyAMD * ((Math.pow(1 + r, n) - 1) / r);
}

function fmt(v: number) {
  return Math.round(v).toLocaleString("hy-AM");
}

// ─── Component ────────────────────────────────────────────────────────────────

const WORKING_DAYS_PER_MONTH = 22;

export default function MoneyClarity() {
  // Selected preset ids → override price (user can edit)
  const [selected, setSelected] = useState<Record<string, number>>({});
  // Preset price overrides (if user taps the price to edit)
  const [priceOverrides, setPriceOverrides] = useState<Record<string, string>>({});
  // Custom expenses
  const [customs, setCustoms] = useState<CustomExpense[]>([
    { uid: "taxi", name: "Taxi / Օրական տրանսպորտ", amount: "", period: "daily" },
  ]);
  const [investYears, setInvestYears] = useState(10);

  const uid = useId();

  // ── Helpers ─────────────────────────────────────────────────────────────────

  // Convert a preset's native price to AMD for calculations
  const toAMD = (price: number, currency: PriceCurrency) =>
    currency === "USD" ? price * AMD_PER_USD : price;

  const togglePreset = (preset: Preset) => {
    setSelected((prev) => {
      if (prev[preset.id] !== undefined) {
        const next = { ...prev };
        delete next[preset.id];
        mixpanel.track("money_clarity_subscription_removed", { subscription: preset.id });
        return next;
      }
      const rawOverride = priceOverrides[preset.id];
      const nativePrice = rawOverride ? Number(rawOverride) || preset.price : preset.price;
      const amd = toAMD(nativePrice, preset.currency);
      mixpanel.track("money_clarity_subscription_added", { subscription: preset.id });
      return { ...prev, [preset.id]: amd };
    });
  };

  const updateSelectedPrice = (preset: Preset, raw: string) => {
    setPriceOverrides((p) => ({ ...p, [preset.id]: raw }));
    const num = Number(raw);
    if (!isNaN(num) && num >= 0) {
      setSelected((prev) =>
        prev[preset.id] !== undefined
          ? { ...prev, [preset.id]: toAMD(num, preset.currency) }
          : prev
      );
    }
  };

  const addCustom = () => {
    setCustoms((prev) => [
      ...prev,
      { uid: `custom-${Date.now()}`, name: "", amount: "", period: "monthly" },
    ]);
  };

  const removeCustom = (uid: string) => {
    setCustoms((prev) => prev.filter((c) => c.uid !== uid));
  };

  const updateCustom = (uid: string, field: keyof CustomExpense, value: string) => {
    setCustoms((prev) =>
      prev.map((c) => (c.uid === uid ? { ...c, [field]: value } : c))
    );
  };

  // ── Calculations ─────────────────────────────────────────────────────────────

  const subscriptionTotal = Object.values(selected).reduce((a, b) => a + b, 0);

  const customTotal = customs.reduce((sum, c) => {
    const amt = parseFloat(c.amount) || 0;
    return sum + (c.period === "daily" ? amt * WORKING_DAYS_PER_MONTH : amt);
  }, 0);

  const totalMonthly = subscriptionTotal + customTotal;
  const totalYearly = totalMonthly * 12;

  const projectionYears = [1, 3, 5, 10, 20, 30];
  const chartData = projectionYears.map((y) => ({
    year: `${y} տ.`,
    invested: Math.round(totalMonthly * 12 * y),
    portfolio: Math.round(sp500FV(totalMonthly, y)),
  }));

  const highlight = sp500FV(totalMonthly, investYears);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <>
      <SimpleHeader
        onHome={() => (window.location.href = "/")}
        onResources={() => (window.location.href = "/resources")}
      />

      <div className="mc-root">
        {/* ── Background blob ── */}
        <div className="mc-blob" />

        {/* ── Hero ── */}
        <div className="mc-hero">
          <h1 className="mc-title">
            Ֆինանսական<br />
            <span className="mc-title-gradient">Հստակություն</span>
          </h1>
          <p className="mc-subtitle">
            Ընտրիր քո բաժանորդագրությունները, ավելացրու ամենօրյա ծախսերը և
            տես՝ S&P 500-ում ներդնելու դեպքում ինչ կունենայիր:
          </p>
        </div>

        <div className="mc-layout">
          {/* ═══════════════════════════════════════════════════════════
              LEFT COLUMN – inputs
          ═══════════════════════════════════════════════════════════ */}
          <div className="mc-inputs">

            {/* ── Subscription presets ── */}
            <section className="mc-card">
              <h2 className="mc-card-title">📱 Բաժանորդագրություններ</h2>
              <p className="mc-card-hint">
                Ընտրիր քո ծառայությունները։ Գինը կարող ես խմբագրել։
              </p>

              {CATEGORIES.map((cat) => (
                <div key={cat} className="mc-cat-group">
                  <div className="mc-cat-label">{cat}</div>
                  <div className="mc-preset-grid">
                    {PRESETS.filter((p) => p.category === cat).map((preset) => {
                      const isOn = selected[preset.id] !== undefined;
                      const displayPrice = priceOverrides[preset.id] ?? String(preset.price);
                      const sym = preset.currency === "USD" ? "$" : "֏";
                      return (
                        <div
                          key={preset.id}
                          className={`mc-preset-card ${isOn ? "mc-preset-on" : ""}`}
                          onClick={() => togglePreset(preset)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && togglePreset(preset)}
                        >
                          <span className="mc-preset-emoji">{preset.emoji}</span>
                          <span className="mc-preset-name">{preset.name}</span>
                          {preset.note && (
                            <span className="mc-preset-note">{preset.note}</span>
                          )}
                          <div
                            className="mc-preset-price-row"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {preset.currency === "USD" && (
                              <span className="mc-preset-dram">{sym}</span>
                            )}
                            <input
                              className="mc-preset-price-input"
                              type="number"
                              value={displayPrice}
                              onChange={(e) =>
                                updateSelectedPrice(preset, e.target.value)
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isOn) togglePreset(preset);
                              }}
                              aria-label={`${preset.name} price`}
                            />
                            {preset.currency === "AMD" && (
                              <span className="mc-preset-dram">{sym}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>

            {/* ── Custom expenses ── */}
            <section className="mc-card">
              <h2 className="mc-card-title">🧾 Կանոնավոր ծախսեր</h2>
              <p className="mc-card-hint">
                Taxi, սուրճ, ֆիտնես, delivery — ամեն ինչ, որ ամեն ամիս է:
              </p>

              <div className="mc-customs-list">
                {customs.map((c) => (
                  <div key={c.uid} className="mc-custom-row">
                    <input
                      className="mc-custom-name"
                      type="text"
                      placeholder="Ծախսի անուն"
                      value={c.name}
                      onChange={(e) => updateCustom(c.uid, "name", e.target.value)}
                    />
                    <input
                      className="mc-custom-amount"
                      type="number"
                      placeholder="0"
                      value={c.amount}
                      min="0"
                      onChange={(e) => updateCustom(c.uid, "amount", e.target.value)}
                    />
                    <span className="mc-custom-dram">֏</span>
                    <select
                      className="mc-custom-period"
                      value={c.period}
                      onChange={(e) =>
                        updateCustom(c.uid, "period", e.target.value)
                      }
                    >
                      <option value="daily">/ օր × {WORKING_DAYS_PER_MONTH}</option>
                      <option value="monthly">/ ամիս</option>
                    </select>
                    <button
                      className="mc-custom-remove"
                      onClick={() => removeCustom(c.uid)}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <button className="mc-add-btn" onClick={addCustom}>
                + Ավելացնել
              </button>
            </section>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              RIGHT COLUMN – results
          ═══════════════════════════════════════════════════════════ */}
          <div className="mc-results">

            {/* ── Monthly total ── */}
            <div className="mc-total-card">
              <div className="mc-total-label">Ամսական ծախս</div>
              <div className="mc-total-amount">֏{fmt(totalMonthly)}</div>
              <div className="mc-total-year">
                Տարեկան՝ <strong>֏{fmt(totalYearly)}</strong>
              </div>

              {totalMonthly > 0 && (
                <div className="mc-total-breakdown">
                  {subscriptionTotal > 0 && (
                    <div className="mc-breakdown-row">
                      <span>📱 Բաժանորդ.</span>
                      <span>֏{fmt(subscriptionTotal)}</span>
                    </div>
                  )}
                  {customTotal > 0 && (
                    <div className="mc-breakdown-row">
                      <span>🧾 Ծախսեր</span>
                      <span>֏{fmt(customTotal)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {totalMonthly > 0 && (
              <>
                {/* ── S&P 500 highlight ── */}
                <div className="mc-sp500-card">
                  <div className="mc-sp500-header">
                    <span className="mc-sp500-icon">📈</span>
                    <div>
                      <div className="mc-sp500-title">
                        S&P 500-ում ներդնելու դեպքում
                      </div>
                      <div className="mc-sp500-subtitle">
                        Պատմական միջին տոկոս՝ ~10% / տարի
                      </div>
                    </div>
                  </div>

                  <div className="mc-years-toggle">
                    {[1, 5, 10, 20, 30].map((y) => (
                      <button
                        key={y}
                        className={`mc-year-btn ${investYears === y ? "mc-year-btn-on" : ""}`}
                        onClick={() => setInvestYears(y)}
                      >
                        {y} տ.
                      </button>
                    ))}
                  </div>

                  <div className="mc-sp500-result">
                    <div className="mc-sp500-amount">֏{fmt(highlight)}</div>
                    <div className="mc-sp500-note">
                      {investYears} տարի անց (ներդրվածը՝{" "}
                      <strong>֏{fmt(totalMonthly * 12 * investYears)}</strong>)
                    </div>
                  </div>
                </div>

                {/* ── Chart ── */}
                <div className="mc-chart-card">
                  <h3 className="mc-chart-title">Աճ ըստ տարիների</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id={`${uid}-portfolio`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F2650B" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#F2650B" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id={`${uid}-invested`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                      <YAxis
                        tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}M`}
                        tick={{ fontSize: 11 }}
                        width={40}
                      />
                      <Tooltip
                        formatter={(value: any) => `֏${fmt(value)}`}
                        labelFormatter={(l) => `${l} ամիս`}
                      />
                      <Area
                        type="monotone"
                        dataKey="portfolio"
                        stroke="#F2650B"
                        strokeWidth={2}
                        fill={`url(#${uid}-portfolio)`}
                        name="S&P 500 պորտֆոլ."
                      />
                      <Area
                        type="monotone"
                        dataKey="invested"
                        stroke="#94a3b8"
                        strokeWidth={2}
                        fill={`url(#${uid}-invested)`}
                        name="Ընդ. ներդրված"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* ── Projection table ── */}
                <div className="mc-table-card">
                  <h3 className="mc-chart-title">Մանրամասն</h3>
                  <div className="mc-table-scroll">
                    <table className="mc-table">
                      <thead>
                        <tr>
                          <th>Տարի</th>
                          <th>Ներդրված</th>
                          <th>S&P 500 կանխատ.</th>
                          <th>Շահույթ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectionYears.map((y) => {
                          const inv = totalMonthly * 12 * y;
                          const fv = sp500FV(totalMonthly, y);
                          return (
                            <tr key={y}>
                              <td>{y} տ.</td>
                              <td>֏{fmt(inv)}</td>
                              <td className="mc-table-highlight">֏{fmt(fv)}</td>
                              <td className="mc-table-gain">+֏{fmt(fv - inv)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="mc-disclaimer">
                    * Հաշվարկը կատարվել է S&P 500-ի պատմական ~10% տարեկան
                    տոկոսի հիման վրա: Ֆոնդի արդյունքները կարող են տարբերվել:
                    Հարկերը և միջնորդավճարները հաշվի չեն առնված:
                  </div>
                </div>
              </>
            )}

            {totalMonthly === 0 && (
              <div className="mc-empty">
                <div className="mc-empty-icon">👆</div>
                <div>Ընտրիր բաժանորդագրություններ կամ ավելացրու ծախսեր</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
